import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, ToggleButton, ToggleButtonGroup } from "@mui/material";
import VideoModal from '../components/VideoModal';
import { atlassianData, tabs } from './AtlassianData';
import './atlassian.css';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ display: value === index ? 'block' : 'none', pt: 0 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const AtlassianDemoPage = () => {
    useEffect(() => {
        document.body.classList.add("no-scroll");
        return () => {
            document.body.classList.remove("no-scroll");
        };
    }, []);

    const url = new URL(window.location.href);

    const [value, setValue] = useState(0);
    const [searchResults, setSearchResults] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [open, setOpen] = useState(false);
    const [demoData, setDemoData] = useState();
    const [deployTarget, setDeployTarget] = useState("cloud");

    // Set initial tab based on URL
    useEffect(() => {
        const tabParam = new URLSearchParams(window.location.search).get("tab");
        const tabIndex = tabs.findIndex(tab => tab.value === tabParam);
        if (tabIndex !== -1) {
            setValue(tabIndex);
        }
    }, []);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        const selectedTab = tabs[newValue];
        if (selectedTab?.value) {
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("tab", selectedTab.value);
            window.history.pushState({}, '', newUrl.toString());
        }
    };

    const handleOpen = (videoData) => {
        setDemoData(videoData);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const filterCards = (atlassianData, searchValue) => {
        if (!searchValue.trim()) return [];
        return atlassianData.filter(card =>
            card.text.toLowerCase().includes(searchValue.toLowerCase()) ||
            (card.tags && card.tags.some(tag => tag.toLowerCase().includes(searchValue.toLowerCase())))
        );
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchValue(value);
        if (!value.trim()) {
            setSearchResults([]);
            return;
        }
        const filteredCards = filterCards(atlassianData, value);
        setSearchResults(filteredCards);
    };

    const filteredCards = searchValue.trim() ? filterCards(atlassianData, searchValue) : [];

    const currentTabLabel = tabs[value]?.label;
    const showDeployToggle =
        Boolean(currentTabLabel) &&
        atlassianData.some(
            (item) => item.cardType === currentTabLabel && item.deployType
        );

    const matchesTabAndDeploy = (item, tabLabel) => {
        if (item?.cardType !== tabLabel) return false;
        if (!item?.deployType) return true;
        return item.deployType === deployTarget;
    };

    return (
        <>
            <div className="backButton d-flex align-items-center mx-2 my-3"
                onClick={() => window.location.href = `${url.origin}/demo`}
                style={{ overflow: "hidden" }}
            >
                <span className="material-symbols-rounded text-dark">keyboard_arrow_left</span>
                <p className="mb-0">Back to main page</p>
            </div>

            <div className='w-100 bg-light px-5'>
                <div className='row justify-content-between align-items-center w-100 py-4 px-5'>
                    <div className='col-6'>
                        <h4 className='text-start mt-2 h-auto lh-base text-secondary-emphasis fw-bold mx-4 mx-md-0 mt-4 mt-md-0'>
                            Atlassian Demo
                        </h4>
                    </div>
                    <div className='col-4'>
                        <input className="form-control p-3" placeholder="Search" onChange={handleSearchChange} />
                    </div>
                </div>
            </div>

            <div className='px-sm-4 px-0 pt-0 scrolling-main-sec'>
                {searchValue?.length === 0 && (
                    <div className='mt-5 px-0 px-sm-5 d-flex flx-grow'>
                        <div className='justify-content-end ms-2 ms-sm-5 atlassian-sidebar-tabs-wrap'>
                            <Tabs
                                value={value}
                                orientation='vertical'
                                className='border-end'
                                onChange={handleChange}
                                TabIndicatorProps={{
                                    style: {
                                        backgroundColor: '#EB5424',
                                        width: '3px',
                                        borderRight: '1px solid #CBCBCB',
                                    },
                                }}
                                sx={{
                                    '& .MuiTabs-indicator': {
                                        left: 0,
                                        right: 'auto',
                                    },
                                }}
                                aria-label="basic tabs example"
                            >
                                {tabs?.map((tab, index) => (
                                    <Tab
                                        key={index}
                                        className='font-14 py-3 text-capitalize'
                                        label={<div className='w-100 text-start'>{tab?.label}</div>}
                                        disableRipple
                                        sx={{
                                            color: 'gray',
                                            fontFamily: 'inherit',
                                            fontWeight: 600,
                                            '&.Mui-selected': { color: '#EB5424' },
                                            paddingBlockEnd: 4,
                                            paddingInlineStart: 2,
                                            paddingInlineEnd: 4,
                                        }}
                                        {...a11yProps(index)}
                                    />
                                ))}
                            </Tabs>
                        </div>

                        <div className='col px-4 scrolling-sec d-flex flex-column'>
                            {showDeployToggle && (
                                <div className="atlassian-deploy-toggle-row d-flex justify-content-end flex-shrink-0 mb-3 pe-1">
                                    <ToggleButtonGroup
                                        value={deployTarget}
                                        exclusive
                                        onChange={(_, next) => {
                                            if (next !== null) setDeployTarget(next);
                                        }}
                                        aria-label="Atlassian deployment"
                                        size="small"
                                        sx={{
                                            "& .MuiToggleButton-root": {
                                                fontFamily: "inherit",
                                                fontWeight: 600,
                                                textTransform: "none",
                                                px: 2,
                                                py: 0.75,
                                                color: "gray",
                                                borderColor: "#CBCBCB",
                                            },
                                            "& .MuiToggleButton-root.Mui-selected": {
                                                color: "#EB5424",
                                                backgroundColor: "rgba(235, 84, 36, 0.08)",
                                            },
                                            "& .MuiToggleButton-root.Mui-selected:hover": {
                                                backgroundColor: "rgba(235, 84, 36, 0.14)",
                                            },
                                        }}
                                    >
                                        <ToggleButton value="cloud">Cloud</ToggleButton>
                                        <ToggleButton value="dc">Data Center</ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                            )}
                            <div className="flex-grow-1" style={{ minHeight: 0 }}>
                            {tabs?.map((tab, index) => (
                                <TabPanel value={value} index={index} key={index}>
                                    <div className="mx-sm-4">
                                        <div className="row g-4">
                                            {atlassianData
                                                ?.filter(item => matchesTabAndDeploy(item, tab?.label))
                                                ?.map((item, idx) => (
                                                    <div key={idx} className="col-sm-12 col-md-6 col-lg-4">
                                                        <div className="webinar" onClick={() => handleOpen(item)}>
                                                            <div className="webinar-image">{item?.icon}</div>
                                                            <div className="webinar-heading">
                                                                <h3>{item?.text}</h3>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </TabPanel>
                            ))}
                            </div>
                        </div>
                    </div>
                )}

                {searchValue.trim() && searchResults.length > 0 && (
                    <div className="mx-sm-4">
                        <div className="row g-4 scrolling-sec">
                            {searchResults.map((item, index) => (
                                <div key={index} className="col-sm-12 col-md-6 col-lg-4">
                                    <div className="webinar" onClick={() => handleOpen(item)}>
                                        <div className="webinar-image">{item?.icon}</div>
                                        <div className="webinar-heading">
                                            <h3>{item?.text}</h3>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {open &&
                <VideoModal
                    open={open}
                    handleOpen={handleOpen}
                    handleClose={handleClose}
                    videoData={demoData}
                />
            }
        </>
    );
};